import React, { useEffect, useState } from "react";
import "./OrderManagement.css";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaCalendarAlt, FaEye, FaEyeSlash, FaEdit, FaSave, FaTimes, FaBox, FaShippingFast, FaMoneyBillWave, FaSpinner } from "react-icons/fa";
import ClipLoader from "react-spinners/ClipLoader";

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [expandedOrderIndex, setExpandedOrderIndex] = useState(null);
  const [updateFormIndex, setUpdateFormIndex] = useState(null);
  const [updateData, setUpdateData] = useState({
    orderStatus: "",
    expectedDeliveryDate: "",
    deliveryTrackingNumber: "",
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDateQuery, setSearchDateQuery] = useState('');
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const goToNextPage = () => {
    if (hasNextPage) {
      setCurrentPage((prevPage) => prevPage + 1);
      window.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem("admintoken");
        if (!token) {
          throw new Error("Authentication token not found. Please login again.");
        }

        // const response = await fetch(
        //   `https://admin.riymay.com/api/v1/admin/order/management/orders?page=${currentPage}&razorpayOrderId=${searchQuery}&startDate=${searchDateQuery}`,
        //   {
        //     method: "GET",
        //     headers: {
        //       "Content-Type": "application/json",
        //       Authorization: `Bearer ${token}`,
        //     },
        //   }
        // );
        const response = await fetch(
          `https://admin.riymay.com/api/v1/admin/order/management/orders?page=${currentPage}&cashfreeOrderId=${searchQuery}&startDate=${searchDateQuery}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch orders: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.success) {
          setOrders(data.data);
          setHasNextPage(data.nextPage);
          setTotalOrders(data.totalOrders || 0);
          setTotalPages(data.totalPages || 1);
        } else {
          throw new Error(data.message || "Failed to fetch orders.");
        }
      } catch (err) {
        setError(err.message);
        toast.error(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [currentPage, searchQuery, searchDateQuery]);

  const toggleExpand = (index) => {
    setExpandedOrderIndex(expandedOrderIndex === index ? null : index);
  };

  const toggleUpdateForm = (index) => {
    const order = orders[index];
    setUpdateFormIndex(updateFormIndex === index ? null : index);
    setUpdateData({
      orderStatus: order.orderInfo.orderStatus || "",
      expectedDeliveryDate: order.orderShippingInfo?.expectedDeliveryDate
        ? new Date(order.orderShippingInfo.expectedDeliveryDate).toISOString().split("T")[0]
        : "",
      deliveryTrackingNumber: order.orderShippingInfo?.deliverytrackingNumber || "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleUpdateOrder = async (orderInfoId) => {
    // Validate form data
    if (!updateData.orderStatus) {
      toast.error("Please select an order status");
      return;
    }

    setActionLoading(orderInfoId);
    const token = localStorage.getItem("admintoken");
    
    try {
      const response = await fetch(
        "https://admin.riymay.com/api/v1/admin/order/management/update/order/status",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            orderinfoId: orderInfoId,
            orderStatus: updateData.orderStatus,
            expectedDeliveryDate: updateData.expectedDeliveryDate,
            deliverytrackingNumber: updateData.deliveryTrackingNumber,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update order.");
      }

      const data = await response.json();

      if (data.success) {
        toast.success("Order updated successfully");
        setUpdateFormIndex(null); // Close the form
        
        // Update the local state to reflect the changes
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.orderInfo._id === orderInfoId
              ? {
                  ...order,
                  orderInfo: {
                    ...order.orderInfo,
                    orderStatus: updateData.orderStatus,
                  },
                  orderShippingInfo: {
                    ...order.orderShippingInfo,
                    expectedDeliveryDate: updateData.expectedDeliveryDate,
                    deliverytrackingNumber: updateData.deliveryTrackingNumber,
                  },
                }
              : order
          )
        );
      } else {
        throw new Error(data.message || "Failed to update order.");
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchDateChange = (e) => {
    setSearchDateQuery(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSearchDateQuery('');
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'confirmed': return 'status-confirmed';
      case 'out_for_delivery': return 'status-out-for-delivery';
      case 'completed': return 'status-completed';
      case 'cancelled': return 'status-cancelled';
      case 'pending': return 'status-pending';
      default: return '';
    }
  };

  return (
    <div className="order-management-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="order-management-header">
        <h1><FaBox className="header-icon" /> Order Management</h1>
        
        <div className="search-filters">
          <div className="search-group">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by Order ID..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>
          </div>
          
          <div className="search-group">
            <div className="date-input-wrapper">
              <FaCalendarAlt className="date-icon" />
              <input
                type="date"
                value={searchDateQuery}
                onChange={handleSearchDateChange}
                className="date-input"
                placeholder="Filter by date"
              />
              <label className="date-label">Order Date</label>
            </div>
          </div>
          
          {(searchQuery || searchDateQuery) && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              <FaTimes /> Clear Filters
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="loading-container">
          <ClipLoader color="#0056b3" size={50} />
          <p>Loading orders...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <FaTimes className="error-icon" />
          <p className="error-message">{error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Retry
          </button>
        </div>
      ) : orders.length === 0 ? (
        <div className="no-orders-container">
          <p className="no-orders-message">No orders found matching your criteria.</p>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map((order, index) => {
            const { orderInfo, orderItems, orderPaymentInfo, orderShippingInfo, tryAndBuyOrderInfo } = order;
            const isExpanded = expandedOrderIndex === index;
            const isUpdateFormVisible = updateFormIndex === index;

            return (
              <div key={index} className={`order-card ${isExpanded ? 'expanded' : ''}`}>
                <div className="order-summary" onClick={() => toggleExpand(index)}>
                  <div className="order-summary-left">
                    <div className="order-id">
                      <span className="label">Order ID:</span>
                      <span className="value">{orderInfo._id.substring(orderInfo._id.length - 8)}</span>
                    </div>
                    <div className="order-user">
                      <span className="label">User:</span>
                      <span className="value">{orderInfo.userId.email}</span>
                    </div>
                  </div>
                  
                  <div className="order-summary-center">
                    <div className="order-amount">
                      <span className="label">Amount:</span>
                      <span className="value">₹{orderInfo.totalOrderAmount.toLocaleString()}</span>
                    </div>
                    <div className="order-date">
                      <span className="label">Date:</span>
                      <span className="value">{formatDate(orderInfo.createdAt)}</span>
                    </div>
                  </div>
                  
                  <div className="order-summary-right">
                    <div className="order-status-container">
                      <span className={`order-status ${getStatusClass(orderInfo.orderStatus)}`}>
                        {orderInfo.orderStatus.replace('_', ' ')}
                      </span>
                      <span className="order-type">
                        {tryAndBuyOrderInfo ? "Trial Order" : "Regular Order"}
                      </span>
                    </div>
                    
                    <div className="order-actions">
                      <button 
                        className="view-details-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(index);
                        }}
                      >
                        {isExpanded ? <><FaEyeSlash /> Hide</> : <><FaEye /> View</>}
                      </button>
                      
                      <button 
                        className="update-order-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleUpdateForm(index);
                        }}
                      >
                        {isUpdateFormVisible ? <><FaTimes /> Cancel</> : <><FaEdit /> Update</>}
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="order-details">
                    <div className="order-details-section">
                      <div className="section-header">
                        <FaBox className="section-icon" />
                        <h3>Order Items ({orderItems.length})</h3>
                      </div>
                      
                      <div className="order-items-container">
                        <table className="order-items-table">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Quantity</th>
                              <th>Unit Price</th>
                              <th>Total Price</th>
                              <th>Size</th>
                              <th>Color</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderItems.map((item, idx) => (
                              <tr key={idx}>
                                <td className="product-cell">{item.productName}</td>
                                <td>{item.quantity}</td>
                                <td>₹{item.unitPrice.toLocaleString()}</td>
                                <td>₹{item.totalPrice.toLocaleString()}</td>
                                <td>{item.size || "N/A"}</td>
                                <td>{item.color || "N/A"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="order-details-grid">
                      <div className="order-details-section">
                        <div className="section-header">
                          <FaShippingFast className="section-icon" />
                          <h3>Shipping Information</h3>
                        </div>
                        
                        <div className="shipping-details">
                          <p className="address-line">
                            <strong>Address:</strong> {orderShippingInfo.orderShippingAddress[0].street}
                          </p>
                          <p className="address-line">
                            {orderShippingInfo.orderShippingAddress[0].city}, {orderShippingInfo.orderShippingAddress[0].state} {orderShippingInfo.orderShippingAddress[0].pinCode}
                          </p>
                          <p className="address-line">
                            {orderShippingInfo.orderShippingAddress[0].country}
                          </p>
                          
                          {orderShippingInfo.expectedDeliveryDate && (
                            <p className="delivery-date">
                              <strong>Expected Delivery:</strong> {formatDate(orderShippingInfo.expectedDeliveryDate)}
                            </p>
                          )}
                          
                          {orderShippingInfo.deliverytrackingNumber && (
                            <p className="tracking-number">
                              <strong>Tracking Number:</strong> {orderShippingInfo.deliverytrackingNumber}
                            </p>
                          )}
                        </div>
                      </div>

                      {orderPaymentInfo && (
                        <div className="order-details-section">
                          <div className="section-header">
                            <FaMoneyBillWave className="section-icon" />
                            <h3>Payment Information</h3>
                          </div>
                          
                          <div className="payment-details">
                            <p><strong>Payment Method:</strong> {orderPaymentInfo.paymentMethod}</p>
                            <p><strong>Payment ID:</strong> {orderPaymentInfo.paymentId}</p>
                            <p><strong>Status:</strong> {orderPaymentInfo.paymentStatus}</p>
                            {orderPaymentInfo.cashfreeOrderId && (
  <p><strong>Cashfree Order ID:</strong> {orderPaymentInfo.cashfreeOrderId}</p>
)}
{orderPaymentInfo.cashfreePaymentId && (
  <p><strong>Cashfree Payment ID:</strong> {orderPaymentInfo.cashfreePaymentId}</p>
)}
                            {/* {orderPaymentInfo.razorpayOrderId && (
                              <p><strong>Razorpay Order ID:</strong> {orderPaymentInfo.razorpayOrderId}</p>
                            )} */}
                          </div>
                        </div>
                      )}

                      {tryAndBuyOrderInfo && (
                        <div className="order-details-section">
                          <div className="section-header">
                            <h3>Trial Order Information</h3>
                          </div>
                          
                          <div className="trial-details">
                            <p><strong>Total Trial Items:</strong> {tryAndBuyOrderInfo.totalTrialItems}</p>
                            <p><strong>Trial Status:</strong> {tryAndBuyOrderInfo.trialStatus}</p>
                            {tryAndBuyOrderInfo.trialExpectedDate && (
                              <p><strong>Trial Expected Date:</strong> {formatDate(tryAndBuyOrderInfo.trialExpectedDate)}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {isUpdateFormVisible && (
                  <div className="update-form">
                    <div className="section-header">
                      <FaEdit className="section-icon" />
                      <h3>Update Order Status</h3>
                    </div>
                    
                    <div className="update-form-grid">
                      <div className="form-group">
                        <label>Order Status</label>
                        <select
                          name="orderStatus"
                          value={updateData.orderStatus}
                          onChange={handleInputChange}
                          className="form-control"
                        >
                          <option value="">Select Status</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="out_for_delivery">Out for Delivery</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label>Expected Delivery Date</label>
                        <input
                          type="date"
                          name="expectedDeliveryDate"
                          value={updateData.expectedDeliveryDate}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Tracking Number</label>
                        <input
                          type="text"
                          name="deliveryTrackingNumber"
                          value={updateData.deliveryTrackingNumber}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Enter tracking number"
                        />
                      </div>
                    </div>
                    
                    <div className="update-form-actions">
                      <button 
                        className="cancel-btn" 
                        onClick={() => setUpdateFormIndex(null)}
                        disabled={actionLoading === orderInfo._id}
                      >
                        <FaTimes /> Cancel
                      </button>
                      
                      <button 
                        className="save-btn" 
                        onClick={() => handleUpdateOrder(orderInfo._id)}
                        disabled={actionLoading === orderInfo._id}
                      >
                        {actionLoading === orderInfo._id ? (
                          <><FaSpinner className="spinner-icon" /> Updating...</>
                        ) : (
                          <><FaSave /> Save Changes</>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && orders.length > 0 && (
        <div className="pagination">
          <button
            className="pagination-btn prev-btn"
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
          >
            &larr; Previous
          </button>
          
          <div className="pagination-info">
            <span>Page {currentPage} of {totalPages || 1}</span>
            <span className="total-orders">{totalOrders} orders</span>
          </div>
          
          <button
            className="pagination-btn next-btn"
            onClick={goToNextPage}
            disabled={!hasNextPage}
          >
            Next &rarr;
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;